import { Alert } from '@zillow/constellation';
import { gql } from '@apollo/client';
import DetailSection from '../DetailSection';
import CurrentIncome from '../../../../../../components/CurrentIncome';
import TagType from '../../../../../../constants/tagType';
import useQueryVariables from '../../../../../../hooks/useQueryVariables';
import { useZgGraphqlQuery } from '../../../../../../hooks/useGraphqlQuery';
import { CardSpinner } from '../../../../../../components/CardSpinner';
import { onQueryError } from '../utils/onQueryError';

const ERROR_MSG = "Sorry, there was an error loading this applicant's current income.";

export const QUERY = gql`
  query IncomeHistory($propertyId: ID!, $applicationId: ID!, $applicantId: ID!, $zgAuthZone: ZgAuthZone!) {
    rentalListing(id: $propertyId) {
      id
      landlordRentalApplication(id: $applicationId) {
        id
        applicant(userId: $applicantId, zgAuthZone: $zgAuthZone) {
          id
          userId
          zgAuthZone
          answers {
            id
            currentIncomes {
              id
              title
              amount
              type
              note
              areDocumentsExpired
              isVerified
              verification {
                vendor
                startDate
                endDate
                verifiedDate
              }
              documents {
                id
                expirationDateTime
                previewUrl
                fileName
              }
              ... on EmploymentIncome {
                employer {
                  organization
                }
              }
            }
          }
        }
      }
    }
  }
`;

export default function Income() {
  const queryVariables = useQueryVariables();
  const heading = 'Income';

  const { data, isLoading, isError, error } = useZgGraphqlQuery(QUERY, queryVariables);

  if (isError) {
    onQueryError(error);
  }

  let tagType = TagType.selfReported;
  const isVerifiedIncome = data?.rentalListing?.landlordRentalApplication?.applicant?.answers?.currentIncomes?.some(
    ({ isVerified }) => isVerified,
  );
  if (isVerifiedIncome) {
    tagType = TagType.incomeVerifiedByFinicity;
  }

  if (isLoading)
    return (
      <DetailSection heading={heading}>
        <CardSpinner />
      </DetailSection>
    );
  if (isError) return <Alert>{ERROR_MSG}</Alert>;

  const currentIncomes = data.rentalListing.landlordRentalApplication.applicant.answers.currentIncomes;
  // sort the array so that verified incomes appear first, then housing vouchers, and finally all other incomes
  const sortedCurrentIncomes = [
    ...currentIncomes.filter((currentIncome) => currentIncome.isVerified),
    ...currentIncomes.filter(({ type, isVerified }) => type === 'voucher' && !isVerified),
    ...currentIncomes.filter(({ type, isVerified }) => type !== 'voucher' && !isVerified),
  ];

  return (
    <DetailSection heading={heading} tagType={tagType} id="income-history" className="detail-section__income">
      <>
        {sortedCurrentIncomes.map((currentIncome) => (
          <CurrentIncome currentIncome={currentIncome} key={currentIncome.id} />
        ))}
      </>
    </DetailSection>
  );
}
